/**
 * Boat connection presence (cloud_ftue_base 8.5). Shared by the dashboard list,
 * the boat detail view, and (later) the cloud reveal so they agree on what
 * "connecting" vs "offline" means.
 *
 * A freshly-claimed boat has not stamped `last_contacted_at` yet — core is still
 * booting and dialing the cloud WS. Without a grace state it would read
 * "Offline" the instant it's claimed, contradicting the "your boat is
 * connecting" copy on the activate screen. So a never-contacted boat still
 * within a short grace window after `created_at` reads as `connecting`, only
 * flipping to `offline` once that window elapses.
 */
export type BoatPresence = 'live' | 'online' | 'connecting' | 'offline';

// Live = a WS sample arrived very recently. Online = a contact within the
// tolerated telemetry window (a missed 60s window before flipping). Connecting =
// claimed but not yet heard from, still within the grace window after claim.
const LIVE_THRESHOLD_MS = 15_000;
const ONLINE_THRESHOLD_MS = 90_000;
const CONNECTING_GRACE_MS = 90_000;

export function getBoatPresence(
  lastContactedIso: string | null,
  createdAtIso: string,
  liveSampleAtMs: number | undefined,
  now: number
): BoatPresence {
  if (liveSampleAtMs !== undefined && now - liveSampleAtMs <= LIVE_THRESHOLD_MS) {
    return 'live';
  }
  if (lastContactedIso !== null) {
    return now - new Date(lastContactedIso).getTime() <= ONLINE_THRESHOLD_MS ? 'online' : 'offline';
  }
  // Never contacted. A just-claimed boat is still connecting; only call it
  // offline once the grace window after claim has elapsed — so a normal first
  // run never opens on "Offline".
  return now - new Date(createdAtIso).getTime() <= CONNECTING_GRACE_MS ? 'connecting' : 'offline';
}

export function boatPresenceLabel(p: BoatPresence): string {
  switch (p) {
    case 'live':
      return 'Live';
    case 'online':
      return 'Online';
    case 'connecting':
      return 'Connecting';
    case 'offline':
      return 'Offline';
  }
}
